import { useState, useRef, useEffect } from 'react';

interface Size {
	width: number;
	height: number;
}

interface ResizeOptions {
	aspectRatio?: number;
	minWidth?: number;
	maxWidth?: number;
}

const useResize = (
	options: ResizeOptions = {}
): { size: Size; onMouseDown: (e: React.MouseEvent) => void; resizing: boolean; finalSize: Size } => {
	const { aspectRatio, minWidth = 0, maxWidth = Infinity } = options;
	const lockRatio = !!aspectRatio;
	const [size, setSize] = useState<Size>({
		width: 300,
		height: Math.floor(lockRatio && aspectRatio ? 300 / aspectRatio : 300),
	});
	const [finalSize, setFinalSize] = useState<Size>({
		width: 300,
		height: Math.floor(lockRatio && aspectRatio ? 300 / aspectRatio : 300),
	});
	const [resizing, setResizing] = useState(false);
	const initialSizeRef = useRef<Size>({
		width: 300,
		height: Math.floor(lockRatio && aspectRatio ? 300 / aspectRatio : 300),
	});
	const initialMouseRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

	useEffect(() => {
		if (aspectRatio) {
			const val = { width: 300, height: Math.floor(lockRatio && aspectRatio ? 300 / aspectRatio : 300) };
			setSize(val);
			setFinalSize(val);
			initialSizeRef.current = val;
		}
	}, [lockRatio, aspectRatio]);

	const onMouseDown = (e: React.MouseEvent) => {
		initialSizeRef.current = { ...size };
		initialMouseRef.current = { x: e.clientX, y: e.clientY };
		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp);
		setResizing(true);
	};

	const onMouseUp = () => {
		document.removeEventListener('mousemove', onMouseMove);
		document.removeEventListener('mouseup', onMouseUp);
		setResizing(false);
	};

	useEffect(() => {
		if (!resizing) {
			setFinalSize({ ...size });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resizing, size]);

	const onMouseMove = (e: MouseEvent) => {
		const deltaX = e.clientX - initialMouseRef.current.x;
		const deltaY = e.clientY - initialMouseRef.current.y;

		let newWidth = initialSizeRef.current.width + deltaX;
		let newHeight = initialSizeRef.current.height + deltaY;

		// Clamp width to min and max
		newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

		if (lockRatio && aspectRatio) {
			if (Math.abs(deltaX) > Math.abs(deltaY)) {
				newHeight = Math.floor(newWidth / aspectRatio);
			} else {
				newHeight = Math.floor(newWidth / aspectRatio);
			}
		}

		setSize({ width: newWidth, height: newHeight });
	};

	return { size, onMouseDown, resizing, finalSize };
};

export default useResize;
